import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Badge } from "@shared/ui/badge";
import { Alert, AlertDescription } from "@shared/ui/alert";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  AlertTriangle,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useSafetyProtocols,
  useCreateSafetyProtocol,
  useUpdateSafetyProtocol,
  useToggleSafetyProtocolStatus,
} from "../../../../hooks";
import { SafetyProtocol } from "../../../../types";
import { logEvent } from "@shared/analytics/analytics";

// ====================== TYPES ======================

interface SafetyProtocolFormData {
  name: string;
  description: string;
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== COMPONENTS ======================

// Safety Protocol card component for better reusability
const SafetyProtocolCard: React.FC<{
  protocol: SafetyProtocol;
  onEdit: (protocol: SafetyProtocol) => void;
  onToggleActive: (protocol: SafetyProtocol) => void;
  isUpdating: boolean;
  currentProtocol: SafetyProtocol | null;
}> = ({ protocol, onEdit, onToggleActive, isUpdating, currentProtocol }) => {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group">
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/20">
          <div className="flex justify-between items-start">
            <div>
              <h5 className="font-medium">{protocol.name}</h5>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {protocol.description}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(protocol)}
              className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
              disabled={isUpdating}
            >
              {isUpdating && currentProtocol?.id === protocol.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="p-4">
          <div className="flex gap-2 items-center text-sm text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            <p>{protocol.createdBy}</p>
          </div>
          <div className="flex gap-2 items-center text-sm text-muted-foreground mt-1">
            <Calendar className="h-3.5 w-3.5" />
            <p>{protocol.createdDate}</p>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <span className="text-sm">
              {protocol.isActive ? (
                <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                  <X className="h-3 w-3" /> Inactive
                </span>
              )}
            </span>
            <Switch
              checked={protocol.isActive}
              onCheckedChange={() => onToggleActive(protocol)}
              disabled={isUpdating}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
}> = ({ hasSearchQuery, onAddNew }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <AlertTriangle className="h-10 w-10 text-muted-foreground/50" />
        <h4>No safety protocols found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new safety protocol"
            : "No safety protocols available"}
        </p>
        <Button variant="outline" className="mt-2" onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Protocol
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const SafetyProtocolsTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState<SafetyProtocol | null>(
    null
  );
  
  // Add state to track if we're in cancel mode
  const [isCanceling, setIsCanceling] = useState(false);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: protocols = [] } = useSafetyProtocols(searchQuery);

  const createProtocolMutation = useCreateSafetyProtocol();
  const updateProtocolMutation = useUpdateSafetyProtocol();
  const toggleStatusMutation = useToggleSafetyProtocolStatus();

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "Protocol Name",
        type: "text",
        required: true,
        placeholder: "Enter protocol name",
        minLength: 3,
        maxLength: 100,
        helperText: "Provide a clear, descriptive name for the safety protocol",
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Describe this safety protocol",
        rows: 3,
        maxLength: 500,
        helperText:
          "Detailed description of the safety protocol requirements and procedures",
        className: "pt-2",
      },
    ],
    []
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      description: "",
    },
  });

  // Update form when formData changes - but not during cancel
  useEffect(() => {
    if (isCanceling) return; // Skip form updates during cancel
    
    if (editingProtocol && isEditModalOpen) {
      const populateData = {
        name: editingProtocol.name,
        description: editingProtocol.description,
      };
      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const populateData = {
        name: "",
        description: "",
      };
      FormService.populateForm(form, populateData, formFields);
    }
  }, [editingProtocol, isEditModalOpen, isAddModalOpen, form, formFields, isCanceling]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Prevent any field changes during cancel
    if (isCanceling) return;
  }, [isCanceling]);

  // Handle form submission
  const handleSubmit = async (data: SafetyProtocolFormData) => {
    if (isCanceling) return; // Prevent submit during cancel
    
    try {
      if (editingProtocol) {
        const updatePayload = {
          id: editingProtocol.id,
          name: data.name.trim(),
          description: data.description.trim(),
        };
        // Update existing protocol
        await updateProtocolMutation.mutateAsync(updatePayload, {
          onSuccess: () => {
            logEvent("Safety Protocol Edited", {
              id: editingProtocol.id,
              name: data.name.trim(),
            })
          }
        });
        setIsEditModalOpen(false);
      } else {
        const createPayload = {
          name: data.name.trim(),
          description: data.description.trim(),
        };
        // Create new protocol
        await createProtocolMutation.mutateAsync(createPayload, {
          onSuccess: () => {
            logEvent("Safety Protocol Edited", {
              name: data.name.trim(),
            })
          }
        });
        setIsAddModalOpen(false);
      }

      // Reset form state
      FormService.resetForm(form, formFields);
      setEditingProtocol(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleReset = useCallback(() => {
    if (isCanceling) return; // Prevent reset during cancel
    FormService.resetForm(form, formFields);
  }, [form, formFields, isCanceling]);

  // Handle edit
  const handleEdit = useCallback((protocol: SafetyProtocol) => {
    setEditingProtocol(protocol);
    setIsEditModalOpen(true);
  }, []);

  // Handle toggle status
  const handleToggleStatus = useCallback(
    async (protocol: SafetyProtocol) => {
      try {
        await toggleStatusMutation.mutateAsync({
          id: protocol.id,
          isActive: !protocol.isActive,
          name: protocol.name,
        });
      } catch (error) {
        // Handle error
      }
    },
    [toggleStatusMutation]
  );

  // Handle modal close with cancel protection
  const handleAddModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsAddModalOpen(false);
    setEditingProtocol(null);
    
    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsEditModalOpen(false);
    setEditingProtocol(null);
    
    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    []
  );

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col pb-4">
          <h4 className="page-subheading">Safety Protocols</h4>
          <p className="page-subtitle">Create and manage safety guidelines.</p>
          
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createProtocolMutation.isPending}
        >
          {createProtocolMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Protocol
        </Button>
      </div>

      {/* Protocols Grid */}
      <div className="relative pb-4">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search safety protocols..."
              className="pl-8 w-full sm:w-[250px]"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(protocols) &&
          protocols.map((protocol) => {
            return (
              <SafetyProtocolCard
                key={protocol.id}
                protocol={protocol}
                onEdit={handleEdit}
                onToggleActive={handleToggleStatus}
                isUpdating={
                  updateProtocolMutation.isPending ||
                  toggleStatusMutation.isPending
                }
                currentProtocol={editingProtocol}
              />
            );
          })}

        {Array.isArray(protocols) && protocols.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
          />
        )}
      </div>

      {/* Add Protocol Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Create New Safety Protocol
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createProtocolMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createProtocolMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={createProtocolMutation.isPending}
                  >
                    {createProtocolMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Protocol"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Protocol Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Edit Safety Protocol
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateProtocolMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateProtocolMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={updateProtocolMutation.isPending}
                  >
                    {updateProtocolMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Protocol"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SafetyProtocolsTab;